import seoContent from '@/content/seo-content';
import { buildJsonLd, ogImageUrl } from '@/seo/seo-jsonld';

const { site, services, getPageByPath } = seoContent;

function absoluteUrl(path) {
  if (!path || path === '/') {
    return `${site.origin}/`;
  }
  return `${site.origin}${path}`;
}

function upsertAttribute(tag, attr, key, value) {
  let element = document.head.querySelector(`${tag}[${attr}="${key}"]`);
  if (!element) {
    element = document.createElement(tag);
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', value);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function removeJsonLd() {
  document.head.querySelectorAll('script[data-route-json-ld="true"]').forEach((element) => {
    element.parentNode.removeChild(element);
  });
}

function removeLink(rel) {
  const element = document.head.querySelector(`link[rel="${rel}"]`);
  if (element && element.parentNode) {
    element.parentNode.removeChild(element);
  }
}

export function applyRouteSeo(path) {
  if (typeof document === 'undefined') {
    return;
  }

  const matchedPage = getPageByPath(path);

  // Unknown path (the 404 page, an invalid service slug, or /error): mark the page
  // noindex and strip the canonical and JSON-LD, so crawlers do not index thin
  // not-found pages or treat them as the homepage. Without this, unknown paths fell
  // back to the home page's metadata with index,follow and a canonical pointing home.
  if (!matchedPage) {
    document.title = `Page not found | ${site.siteName}`;
    upsertAttribute('meta', 'name', 'description', 'The page you requested could not be found.');
    upsertAttribute('meta', 'name', 'robots', 'noindex, follow');
    removeLink('canonical');
    removeJsonLd();
    return;
  }

  const page = matchedPage;
  const canonical = absoluteUrl(page.path);
  const image = ogImageUrl;

  document.title = page.title;
  upsertAttribute('meta', 'name', 'description', page.description);
  upsertAttribute('meta', 'name', 'robots', page.robots || 'index,follow');
  upsertAttribute('meta', 'property', 'og:type', 'website');
  upsertAttribute('meta', 'property', 'og:site_name', site.siteName);
  upsertAttribute('meta', 'property', 'og:title', page.title);
  upsertAttribute('meta', 'property', 'og:description', page.description);
  upsertAttribute('meta', 'property', 'og:url', canonical);
  upsertAttribute('meta', 'property', 'og:image', image);
  upsertAttribute('meta', 'name', 'twitter:card', 'summary_large_image');
  upsertAttribute('meta', 'name', 'twitter:title', page.title);
  upsertAttribute('meta', 'name', 'twitter:description', page.description);
  upsertAttribute('meta', 'name', 'twitter:image', image);
  upsertLink('canonical', canonical);

  removeJsonLd();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.routeJsonLd = 'true';
  script.textContent = JSON.stringify(buildJsonLd(page));
  document.head.appendChild(script);
}

export function publicRoutes() {
  return seoContent.pages.map((page) => page.path);
}

export function servicePages() {
  return services;
}
